// src/pages/AdminPanel.jsx
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { db } from "./firebase/firebaseConfig";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  ShoppingCart,
  LogOut,
  Plus,
  FileText,
  Archive,
  ClipboardList,
  Mail,
  Upload,
  BriefcaseBusiness,
  HelpCircle,
  CalendarCheck,
  Menu,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "./context/UserContext";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import LogoutModal from "./components/LogoutModal";

// ===== Styled Components =====
const Container = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(0, 212, 255, 0.14), transparent 28%),
    radial-gradient(circle at bottom right, rgba(255, 122, 24, 0.1), transparent 26%),
    #0a0d12;
  color: #eef4ff;
  font-family: "Inter", "Roboto", sans-serif;

  * {
    box-sizing: border-box;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    overflow: hidden;
  }
`;

/* Sidebar */
const Sidebar = styled.div`
  width: 268px;
  flex: 0 0 268px;
  height: 100vh;
  background: rgba(8, 12, 18, 0.88);
  backdrop-filter: blur(18px);
  display: flex;
  flex-direction: column;
  padding: 24px 18px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 18px 0 45px rgba(0, 0, 0, 0.28);
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.25);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 212, 255, 0.45);
  }

  @media (max-width: 900px) {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 50;
    width: min(84vw, 320px);
    flex: none;
    height: 100vh;
    max-height: none;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    padding: 18px 14px 28px;
    overflow-x: hidden;
    overflow-y: auto;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    border-bottom: none;
    box-shadow: 24px 0 48px rgba(0, 0, 0, 0.42);
    transform: translateX(${({ $open }) => ($open ? "0" : "-105%")});
    transition: transform 0.24s ease;
  }
`;

const SidebarBackdrop = styled.button`
  display: none;

  @media (max-width: 900px) {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    inset: 0;
    z-index: 45;
    border: none;
    background: rgba(0, 0, 0, 0.58);
    backdrop-filter: blur(3px);
    cursor: pointer;
  }
`;

const MobileTopBar = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 62px;
    padding: 10px 14px;
    background: rgba(8, 12, 18, 0.94);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  }
`;

const MobileMenuButton = styled.button`
  width: 40px;
  height: 40px;
  display: inline-grid;
  place-items: center;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.06);
  color: #eef4ff;
  cursor: pointer;
`;

const MobileBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-weight: 800;
  color: #f8fbff;

  img {
    width: 34px;
    height: 34px;
    border-radius: 7px;
    object-fit: contain;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SidebarCloseButton = styled.button`
  display: none;

  @media (max-width: 900px) {
    display: inline-grid;
    place-items: center;
    position: absolute;
    top: 14px;
    right: 14px;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.06);
    color: #eef4ff;
    cursor: pointer;
  }
`;

const SidebarHeader = styled.h2`
  color: #00d4ff;
  margin-bottom: 30px;
  text-align: center;
  font-size: 1.5rem;
`;

const SidebarItem = styled.div`
  padding: 12px 14px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid
    ${({ active }) => (active ? "rgba(0, 212, 255, 0.28)" : "transparent")};
  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(255, 122, 24, 0.14))"
      : "transparent"};
  color: ${({ active }) => (active ? "#f8fbff" : "#9aa8bc")};
  font-weight: ${({ active }) => (active ? "700" : "500")};
  transition: 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #f8fbff;
    transform: translateX(3px);
  }

  @media (max-width: 900px) {
    flex: none;
    min-width: 0;
    margin-bottom: 8px;
    padding: 10px 12px;
    white-space: normal;

    &:hover {
      transform: none;
    }
  }

  @media (max-width: 520px) {
    gap: 8px;
    padding: 9px 10px;
    font-size: 0.86rem;
  }
`;

/* Content */
const Content = styled.div`
  flex: 1;
  min-width: 0;
  height: 100vh;
  padding: 32px;
  overflow-y: auto;
  background:
    radial-gradient(circle at bottom right, rgba(255, 122, 24, 0.08), transparent 28%),
    #0a0d12;

  @media (max-width: 900px) {
    height: calc(100vh - 62px);
    padding: 22px;
  }

  @media (max-width: 520px) {
    height: calc(100vh - 62px);
    padding: 16px 12px 24px;
  }
`;

const Title = styled.h2`
  color: #f8fbff;
  margin-bottom: 20px;
  font-size: 1.85rem;
  font-weight: 800;
  letter-spacing: 0;

  @media (max-width: 520px) {
    font-size: 1.45rem;
    margin-bottom: 14px;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.04));
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 18px 35px rgba(0, 0, 0, 0.18);
  border-radius: 8px;
  padding: 18px;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(0, 212, 255, 0.28);
  }

  h3 {
    font-size: 1.9rem;
    color: #00d4ff;
    margin: 0 0 8px;
  }

  p {
    color: #a8b3c7;
    margin: 0;
    font-weight: 600;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00d4ff, #7dd3fc);
  color: #071018;
  border: none;
  padding: 12px 18px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  margin-bottom: 20px;
  transition: 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 26px rgba(0, 212, 255, 0.22);
  }

  @media (max-width: 520px) {
    width: 100%;
    justify-content: center;
    padding: 11px 14px;
  }
`;

const TableWrapper = styled.div`
  background: rgba(12, 18, 28, 0.78);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 14px;
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
  box-shadow: 0 24px 45px rgba(0, 0, 0, 0.22);

  @media (max-width: 520px) {
    padding: 10px;
    border-radius: 8px;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 0.95rem;

  th,
  td {
    padding: 14px 13px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  th {
    background: rgba(255, 255, 255, 0.06);
    color: #8ee9ff;
    font-size: 0.78rem;
    text-transform: uppercase;
    font-weight: 800;
  }

  tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.025);
  }

  tbody tr {
    transition: background 0.2s ease;
  }

  tbody tr:hover {
    background: rgba(0, 212, 255, 0.08);
  }

  @media (max-width: 520px) {
    min-width: 680px;
    font-size: 0.84rem;

    th,
    td {
      padding: 10px 9px;
    }
  }
`;

const ActionButton = styled.button`
  padding: 7px 12px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: 0.3s;

  ${({ type }) =>
    type === "edit"
      ? `
    background: #00d4ff;
    color: #121212;
    &:hover { background: #00bcd4; }
  `
      : `
    background: #ff5555;
    color: #fff;
    &:hover { background: #e53935; }
  `}
`;
const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;

  img {
    width: 96px;
    height: auto;
    border-radius: 8px;
  }

  @media (max-width: 900px) {
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-start;
    margin: 0 48px 14px 0;

    img {
      width: 42px;
    }
  }
`;

const LeadHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  min-width: 0;

  > div {
    min-width: 0;
  }

  @media (max-width: 520px) {
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const LeadSubtitle = styled.p`
  color: #a8b3c7;
  margin-top: -10px;
  margin-bottom: 0;
  font-weight: 500;
  overflow-wrap: anywhere;

  @media (max-width: 520px) {
    margin-top: -6px;
    font-size: 0.88rem;
  }
`;

const FilterTabs = styled.div`
  display: inline-flex;
  gap: 6px;
  padding: 6px;
  border-radius: 8px;
  background: rgba(12, 18, 28, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  max-width: 100%;
  overflow-x: auto;

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const FilterButton = styled.button`
  border: none;
  border-radius: 6px;
  padding: 9px 14px;
  color: ${({ active }) => (active ? "#121212" : "#d7dee9")};
  background: ${({ active }) =>
    active ? "linear-gradient(135deg, #00d4ff, #6ee7f9)" : "transparent"};
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: ${({ active }) => (active ? "#121212" : "#00d4ff")};
    background: ${({ active }) =>
      active ? "linear-gradient(135deg, #00d4ff, #6ee7f9)" : "rgba(0, 212, 255, 0.08)"};
  }
`;

const LeadStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

const LeadStat = styled.div`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.04));
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.16);

  strong {
    display: block;
    color: #00d4ff;
    font-size: 1.6rem;
    line-height: 1;
    margin-bottom: 6px;
  }

  span {
    color: #b9c2d0;
    font-size: 0.9rem;
  }

  @media (max-width: 520px) {
    padding: 12px;

    strong {
      font-size: 1.25rem;
    }

    span {
      font-size: 0.78rem;
    }
  }
`;

const SourcePill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  color: ${({ type }) => (type === "Quotation" ? "#102018" : "#10202a")};
  background: ${({ type }) =>
    type === "Quotation"
      ? "linear-gradient(135deg, #9ef01a, #70e000)"
      : "linear-gradient(135deg, #7dd3fc, #38bdf8)"};
  font-size: 0.8rem;
  font-weight: 800;
`;

const MessageCell = styled.td`
  min-width: 320px;
  max-width: 460px;
  color: #d8dee9;
  line-height: 1.5;
  white-space: pre-wrap;

  @media (max-width: 520px) {
    min-width: 240px;
    max-width: 300px;
  }
`;

const MessageText = styled.div`
  max-height: ${({ expanded }) => (expanded ? "none" : "4.5em")};
  overflow: hidden;
`;

const ViewMoreButton = styled.button`
  margin-top: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: #00d4ff;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const PhoneCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;
  flex-wrap: wrap;
`;

const CopyButton = styled.button`
  border: 1px solid rgba(0, 212, 255, 0.35);
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 4px 4px;
  flex-wrap: wrap;

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const PaginationInfo = styled.div`
  color: #a8b3c7;
  font-size: 0.9rem;
  font-weight: 600;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 520px) {
    gap: 6px;
  }
`;

const PageButton = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 11px;
  border-radius: 7px;
  border: 1px solid
    ${({ active }) => (active ? "rgba(0, 212, 255, 0.45)" : "rgba(255, 255, 255, 0.1)")};
  background: ${({ active }) =>
    active ? "linear-gradient(135deg, #00d4ff, #7dd3fc)" : "rgba(255, 255, 255, 0.05)"};
  color: ${({ active }) => (active ? "#071018" : "#d7dee9")};
  font-weight: 800;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};
  transition: 0.2s ease;

  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
    border-color: ${({ disabled }) =>
      disabled ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 212, 255, 0.45)"};
  }
`;

const PageSizeSelect = styled.select`
  height: 36px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  padding: 0 10px;
  font-weight: 700;

  @media (max-width: 520px) {
    width: 100%;
  }
`;

const FileHint = styled.span`
  color: #7f8da3;
  font-size: 0.82rem;
  font-weight: 600;
`;

const MediaPreviewGrid = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const MediaThumb = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const DemoVideo = styled.video`
  width: 140px;
  max-height: 80px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const PlanningGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(220px, 1.25fr);
  gap: 12px;
  width: 100%;
  min-width: 0;

  @media (max-width: 980px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const PlanningField = styled.label`
  display: grid;
  gap: 8px;
  min-width: 0;
  color: #a8b3c7;
  font-weight: 700;
  font-size: 0.9rem;
`;

const PlanningInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const PlanningSelect = styled.select`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 13px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #101722;
  color: #eef4ff;
  font-size: 0.95rem;

  &:focus {
    border-color: rgba(0, 212, 255, 0.65);
    outline: none;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.78rem;
  font-weight: 800;
  color: ${({ status }) => (status === "done" ? "#102018" : status === "missed" ? "#2a1010" : "#10202a")};
  background: ${({ status }) =>
    status === "done"
      ? "linear-gradient(135deg, #9ef01a, #70e000)"
      : status === "missed"
      ? "linear-gradient(135deg, #ff9a9e, #ff5c5c)"
      : "linear-gradient(135deg, #7dd3fc, #38bdf8)"};
`;

const TypePill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.78rem;
  font-weight: 800;
  color: #dce8f7;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const TimePill = styled(TypePill)`
  color: #9ee7ff;
  background: rgba(0, 212, 255, 0.08);
  border-color: rgba(0, 212, 255, 0.18);
`;

const PlanningShell = styled.div`
  display: grid;
  gap: 16px;
  min-width: 0;
`;

const PlanningTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  min-width: 0;
`;

const PlanningReport = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
  background: rgba(12, 18, 28, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 14px;

  @media (max-width: 520px) {
    padding: 12px;
  }
`;

const PlanningReportRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  min-width: 0;
`;

const PlanningReportFilters = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const PlanningStatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  min-width: 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const PlanningMiniStat = styled.div`
  min-width: 0;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 10px 12px;

  strong {
    display: block;
    color: #f8fbff;
    font-size: 1.2rem;
    line-height: 1;
    margin-bottom: 5px;
  }

  span {
    color: #8d9bb0;
    font-size: 0.78rem;
    font-weight: 700;
  }
`;

const PlanningDays = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const PlanningDayGroup = styled.section`
  min-width: 0;
  background: rgba(12, 18, 28, 0.64);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
`;

const PlanningDayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  h3 {
    margin: 0;
    color: #f8fbff;
    font-size: 1rem;
    overflow-wrap: anywhere;
  }

  span {
    color: #8d9bb0;
    font-size: 0.82rem;
    font-weight: 700;
  }

  @media (max-width: 520px) {
    padding: 11px 12px;
  }
`;

const PlanningTaskList = styled.div`
  display: grid;
  min-width: 0;
`;

const PlanningTaskItem = styled.div`
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 12px;
  align-items: start;
  min-width: 0;
  padding: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 720px) {
    grid-template-columns: 28px 1fr;
  }

  @media (max-width: 520px) {
    padding: 12px;
    gap: 10px;
  }
`;

const PlanningCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: #00d4ff;
  cursor: pointer;
`;

const PlanningTaskBody = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: ${({ completed }) => (completed ? "#8d9bb0" : "#f8fbff")};
    text-decoration: ${({ completed }) => (completed ? "line-through" : "none")};
    font-size: 0.96rem;
    margin-bottom: 5px;
    overflow-wrap: anywhere;
  }

  p {
    color: #9aa8bc;
    margin: 0;
    line-height: 1.5;
    font-size: 0.9rem;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

const PlanningTaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const PlanningTaskActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 720px) {
    grid-column: 2;
    justify-content: flex-start;
  }
`;

const PlanningIconButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: ${({ danger }) =>
    danger ? "rgba(255, 92, 92, 0.1)" : "rgba(255, 255, 255, 0.045)"};
  color: ${({ danger }) => (danger ? "#ff9a9e" : "#cbd7e8")};
  border-radius: 8px;
  padding: 8px 10px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    border-color: ${({ danger }) =>
      danger ? "rgba(255, 92, 92, 0.45)" : "rgba(0, 212, 255, 0.35)"};
  }
`;

// ===== Main Component =====
const AdminPanel = () => {
  const location = useLocation();
  const getInitialPage = () => {
    if (location.pathname === "/demos") return "demos";
    if (location.pathname === "/careers") return "careers";
    if (location.pathname === "/faqs") return "faqs";
    if (location.pathname === "/planning") return "planning";
    return "dashboard";
  };
  const [activePage, setActivePage] = useState(getInitialPage);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [codeshop, setCodeshop] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [demos, setDemos] = useState([]);
  const [careers, setCareers] = useState([]);
  const [careerApplications, setCareerApplications] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [planningTasks, setPlanningTasks] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useContext(UserContext);
  const [interviewPreps, setInterviewPreps] = useState([]);
  const navigate = useNavigate();

  const [readyCodes, setReadyCodes] = useState([]);

  const switchPage = (page) => {
    setActivePage(page);
    setSidebarOpen(false);
  };

  const fetchReadyCodes = async () => {
    const snap = await getDocs(collection(db, "readyCodes"));
    setReadyCodes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchCourses = async () => {
    const snap = await getDocs(collection(db, "courses"));
    setCourses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };
  const fetchBlogs = async () => {
    const snap = await getDocs(collection(db, "blogs"));
    setBlogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };
  const fetchInterviewPreps = async () => {
    const snap = await getDocs(collection(db, "interviewPreps"));
    setInterviewPreps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchUsers = async () => {
    const snap = await getDocs(collection(db, "users"));
    setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };
  const fetchCodeShop = async () => {
    const snap = await getDocs(collection(db, "codeshop"));
    setCodeshop(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchPurchases = async () => {
    const snap = await getDocs(collection(db, "purchases"));
    setPurchases(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchContacts = async () => {
    const snap = await getDocs(collection(db, "contacts"));
    setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchQuotations = async () => {
    const snap = await getDocs(collection(db, "quotations"));
    setQuotations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchDemos = async () => {
    const snap = await getDocs(collection(db, "demos"));
    setDemos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchCareers = async () => {
    const snap = await getDocs(collection(db, "careers"));
    setCareers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchCareerApplications = async () => {
    const snap = await getDocs(collection(db, "careerApplications"));
    setCareerApplications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchFaqs = async () => {
    const snap = await getDocs(collection(db, "faqs"));
    setFaqs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchPlanningTasks = async () => {
    const snap = await getDocs(collection(db, "planningTasks"));
    setPlanningTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const planningTypes = [
    { value: "youtube", label: "YouTube" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "instagram", label: "Instagram" },
    { value: "projects", label: "Projects" },
    { value: "course", label: "Course" },
    { value: "meeting", label: "Meeting" },
    { value: "engineer-codewala-website", label: "Engineer Codewala Website" },
    { value: "engineer-codewala-app", label: "Engineer Codewala App" },
    { value: "saas-platform", label: "SaaS Platform" },
    { value: "seo", label: "SEO" },
    { value: "smo", label: "SMO" },
    { value: "admin-panel", label: "Admin Panel" },
  ];

  const getPlanningTypeLabel = (value) =>
    planningTypes.find((type) => type.value === value)?.label || "Projects";

  const getTodayInput = () => {
    const date = new Date();
    const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offsetDate.toISOString().slice(0, 10);
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = value.toDate?.() || value;
    return date instanceof Date ? date.toLocaleString() : "-";
  };

  const getTime = (value) => {
    const date = value?.toDate?.() || value;
    return date instanceof Date ? date.getTime() : 0;
  };

  const normalizeService = (value) => {
    if (!value) return "-";

    const serviceMap = {
      mobile: "Mobile App Development",
      web: "Web Development",
      backend: "Backend / API Development",
      fullstack: "Full Stack Solutions",
      courses: "Mentorship",
      career: "Portfolio & Career Support",
      quote: "Quotation Request",
      mentorship: "Mentorship Request",
      contact: "Contact Request",
    };

    const normalizedKey = String(value).trim().toLowerCase();
    return (
      serviceMap[normalizedKey] ||
      String(value)
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  useEffect(() => {
    fetchCourses();
    fetchUsers();
    fetchPurchases();
    fetchBlogs(); // 🆕
    fetchCodeShop();
    fetchInterviewPreps();
    fetchReadyCodes();
    fetchContacts();
    fetchQuotations();
    fetchDemos();
    fetchCareers();
    fetchCareerApplications();
    fetchFaqs();
    fetchPlanningTasks();
  }, []);

  // Dashboard Page
  // Dashboard Page with Stats + Charts
  const DashboardPage = () => {
    // ✅ Format purchase data for revenue chart
    const revenueData = purchases.map((p) => ({
      date: p.createdAt?.toDate().toLocaleDateString(),
      amount: p.amount || 0,
    }));

    // ✅ Format user growth data
    const userData = users.map((u) => ({
      date: u.createdAt?.toDate().toLocaleDateString(),
      users: 1,
    }));

    return (
      <>
        <Title>Dashboard Overview</Title>
        <CardGrid>
          <StatCard>
            <h3>{users.length}</h3>
            <p>Total Users</p>
          </StatCard>
          <StatCard>
            <h3>{courses.length}</h3>
            <p>Total Courses</p>
          </StatCard>
          <StatCard>
            <h3>{blogs.length}</h3>
            <p>Total Blogs</p>
          </StatCard>
          <StatCard>
            <h3>{codeshop.length}</h3>
            <p>Total CodeShop Items</p>
          </StatCard>
          <StatCard>
            <h3>{interviewPreps.length}</h3>
            <p>Total Interview Preps</p>
          </StatCard>
          <StatCard>
            <h3>{purchases.length}</h3>
            <p>Total Purchases</p>
          </StatCard>
          <StatCard>
            <h3>{contacts.length + quotations.length}</h3>
            <p>Contact Leads</p>
          </StatCard>
          <StatCard>
            <h3>{demos.length}</h3>
            <p>Total Demos</p>
          </StatCard>
          <StatCard>
            <h3>{careers.length}</h3>
            <p>Career Openings</p>
          </StatCard>
          <StatCard>
            <h3>{careerApplications.length}</h3>
            <p>Career Applications</p>
          </StatCard>
          <StatCard>
            <h3>{faqs.length}</h3>
            <p>Total FAQs</p>
          </StatCard>
          <StatCard>
            <h3>{planningTasks.length}</h3>
            <p>Planning Tasks</p>
          </StatCard>
          <StatCard>
            <h3>₹{purchases.reduce((sum, p) => sum + (p.amount || 0), 0)}</h3>
            <p>Total Revenue</p>
          </StatCard>
        </CardGrid>

        {/* Revenue Chart */}
        <h3 style={{ marginTop: "40px", color: "#00d4ff" }}>
          Revenue Over Time
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="amount" stroke="#00d4ff" />
          </LineChart>
        </ResponsiveContainer>

        {/* User Growth Chart */}
        <h3 style={{ marginTop: "40px", color: "#00d4ff" }}>Users Growth</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={userData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="users" fill="#ff7a18" />
          </BarChart>
        </ResponsiveContainer>
      </>
    );
  };

  const BlogsPage = () => {
    const handleDelete = async (id) => {
      if (window.confirm("Are you sure you want to delete this blog?")) {
        try {
          await deleteDoc(doc(db, "blogs", id));
          alert("Blog deleted ❌");
          fetchBlogs();
        } catch (err) {
          console.error(err);
          alert("Error deleting blog");
        }
      }
    };

    return (
      <>
        <Title>Blogs</Title>
        <AddButton onClick={() => navigate("/add-blog")}>
          <Plus size={18} /> Add New Blog
        </AddButton>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Author</th>
                <th>Date</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((b) => (
                <tr key={b.id}>
                  <td
                    style={{
                      color: "#58a6ff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => navigate(`/blogs/${b.slug}`)}
                  >
                    {b.title}
                  </td>
                  <td>{b.author || "Unknown"}</td>
                  <td>{b.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>
                    <ActionButton
                      type="edit"
                      onClick={() => navigate(`/edit-blog/${b.id}`)}
                    >
                      Edit
                    </ActionButton>
                  </td>
                  <td>
                    <ActionButton
                      type="delete"
                      onClick={() => handleDelete(b.id)}
                    >
                      Delete
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };
  const InterviewPrepsPage = () => {
    const handleDelete = async (id) => {
      if (
        window.confirm("Are you sure you want to delete this interview prep?")
      ) {
        try {
          await deleteDoc(doc(db, "interviewPreps", id));
          alert("Prep deleted ❌");
          fetchInterviewPreps();
        } catch (err) {
          console.error(err);
          alert("Error deleting prep");
        }
      }
    };

    return (
      <>
        <Title>Interview Preps</Title>
        <AddButton onClick={() => navigate("/add-interviewprep")}>
          <Plus size={18} /> Add Interview Prep
        </AddButton>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Date</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {interviewPreps.map((prep) => (
                <tr key={prep.id}>
                  <td
                    style={{
                      color: "#58a6ff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => navigate(`/interview-prep/${prep.id}`)}
                  >
                    {prep.title}
                  </td>
                  <td>{prep.category || "General"}</td>
                  <td>{prep.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>
                    <ActionButton
                      type="edit"
                      onClick={() => navigate(`/edit-interviewprep/${prep.id}`)}
                    >
                      Edit
                    </ActionButton>
                  </td>
                  <td>
                    <ActionButton
                      type="delete"
                      onClick={() => handleDelete(prep.id)}
                    >
                      Delete
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };

  const CodeShopPage = () => {
    const handleDelete = async (id) => {
      if (window.confirm("Are you sure you want to delete this code item?")) {
        try {
          await deleteDoc(doc(db, "codeshop", id));
          alert("Code deleted ❌");
          fetchCodeShop();
        } catch (err) {
          console.error(err);
          alert("Error deleting code");
        }
      }
    };

    return (
      <>
        <Title>Code Shop</Title>
        <AddButton onClick={() => navigate("/add-codeshop")}>
          <Plus size={18} /> Add New Code
        </AddButton>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Date</th>
                <th>File</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {codeshop.map((c) => (
                <tr key={c.id}>
                  <td>{c.title}</td>
                  <td>{c.price === 0 ? "Free" : `₹${c.price}`}</td>
                  <td>{c.createdAt?.toDate().toLocaleDateString()}</td>
                  <td>
                    <a
                      href={c.fileURL}
                      download
                      style={{ color: "#00d4ff", textDecoration: "underline" }}
                    >
                      Download
                    </a>
                  </td>
                  <td>
                    <ActionButton
                      type="edit"
                      onClick={() => navigate(`/edit-codeshop/${c.id}`)}
                    >
                      Edit
                    </ActionButton>
                  </td>
                  <td>
                    <ActionButton
                      type="delete"
                      onClick={() => handleDelete(c.id)}
                    >
                      Delete
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };

  const handleRoleChange = async (id, newRole, currentRole) => {
    if (newRole === currentRole) return; // no change

    const confirmChange = window.confirm(
      `⚠️ Are you sure you want to change this user's role from "${currentRole}" to "${newRole}"?`
    );

    if (!confirmChange) return;

    try {
      const ref = doc(db, "users", id);
      await updateDoc(ref, { role: newRole });
      alert(`✅ Role updated to ${newRole}`);
      fetchUsers(); // refresh users
    } catch (err) {
      console.error("❌ Error updating role:", err);
      alert("Error updating role");
    }
  };
  // Courses Page
  const CoursesPage = () => {
    const handleDelete = async (id) => {
      if (window.confirm("Are you sure you want to delete this course?")) {
        try {
          await deleteDoc(doc(db, "courses", id));
          alert("Course deleted ❌");
          fetchCourses();
        } catch (err) {
          console.error(err);
          alert("Error deleting course");
        }
      }
    };

    return (
      <>
        <Title>Courses</Title>
        <AddButton onClick={() => navigate("/add-course")}>
          <Plus size={18} /> Add New Course
        </AddButton>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id}>
                  <td
                    style={{
                      color: "#58a6ff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => navigate(`/courses/${c.id}`)}
                  >
                    {c.title}
                  </td>
                  <td>₹{c.price}</td>
                  <td>{c.discount || 0}%</td>
                  <td>
                    <ActionButton
                      type="edit"
                      onClick={() => navigate(`/edit-course/${c.id}`)}
                    >
                      Edit
                    </ActionButton>
                  </td>
                  <td>
                    <ActionButton
                      type="delete"
                      onClick={() => handleDelete(c.id)}
                    >
                      Delete
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };

  // Users Page
  // ================= Users Page ===================
  const UsersPage = () => {
    const [filterRole, setFilterRole] = useState("all");
    const [newAdmin, setNewAdmin] = useState({
      name: "",
      email: "",
      password: "",
    });
    const [addingAdmin, setAddingAdmin] = useState(false);

    // ✅ Filtered users list
    const filteredUsers =
      filterRole === "all"
        ? users
        : users.filter((u) => (u.role || "user") === filterRole);

    // ✅ Add new admin handler
    const handleAddAdmin = async () => {
      if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
        alert("Please fill all fields!");
        return;
      }
      setAddingAdmin(true);
      try {
        // ✅ Create admin in Firebase Auth (you can use Cloud Function for production)
        const res = await fetch(
          "https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=" +
            import.meta.env.VITE_FIREBASE_API_KEY,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: newAdmin.email,
              password: newAdmin.password,
              returnSecureToken: true,
            }),
          }
        );

        const data = await res.json();
        if (data.error) {
          alert(data.error.message);
          setAddingAdmin(false);
          return;
        }

        const uid = data.localId;

        // ✅ Store in Firestore with role = admin
        await setDoc(doc(db, "users", uid), {
          name: newAdmin.name,
          email: newAdmin.email,
          role: "admin",
          createdAt: new Date(),
        });

        alert("✅ Admin user created successfully!");
        setNewAdmin({ name: "", email: "", password: "" });
        fetchUsers(); // refresh list
      } catch (err) {
        console.error("❌ Error creating admin:", err);
        alert("Failed to create admin user.");
      } finally {
        setAddingAdmin(false);
      }
    };

    return (
      <>
        <Title>Users</Title>

        {/* Filter + Add Admin */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <label>Filter by role:</label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: "6px",
                border: "1px solid #444",
                background: "#1a1a1a",
                color: "#fff",
              }}
            >
              <option value="all">All</option>
              <option value="admin">Admins</option>
              <option value="user">Users</option>
            </select>
          </div>

          <button
            style={{
              background: "linear-gradient(135deg, #ff7a18, #ffb347)",
              color: "#000",
              padding: "10px 14px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "600",
            }}
            onClick={() => {
              const modal = document.getElementById("addAdminModal");
              modal.showModal();
            }}
          >
            ➕ Add Admin
          </button>
        </div>

        {/* Users Table */}
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Joined</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.createdAt?.toDate?.().toLocaleDateString?.()}</td>
                  <td>
                    <select
                      value={u.role || "user"} // default to user
                      onChange={(e) =>
                        handleRoleChange(u.id, e.target.value, u.role || "user")
                      }
                      style={{
                        padding: "6px 10px",
                        borderRadius: "6px",
                        border: "1px solid #444",
                        background: "#1a1a1a",
                        color: "#fff",
                      }}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>

        {/* Add Admin Modal */}
        <dialog
          id="addAdminModal"
          style={{
            background: "rgba(18,18,18,0.95)",
            border: "1px solid #333",
            borderRadius: "12px",
            padding: "30px",
            color: "#fff",
            width: "400px",
          }}
        >
          <h3
            style={{
              marginBottom: "20px",
              background: "linear-gradient(90deg,#ff7a18,#ffb347)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Create New Admin
          </h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <input
              type="text"
              placeholder="Name"
              value={newAdmin.name}
              onChange={(e) =>
                setNewAdmin({ ...newAdmin, name: e.target.value })
              }
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #444",
                background: "#1a1a1a",
                color: "#fff",
              }}
            />
            <input
              type="email"
              placeholder="Email"
              value={newAdmin.email}
              onChange={(e) =>
                setNewAdmin({ ...newAdmin, email: e.target.value })
              }
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #444",
                background: "#1a1a1a",
                color: "#fff",
              }}
            />
            <input
              type="password"
              placeholder="Password"
              value={newAdmin.password}
              onChange={(e) =>
                setNewAdmin({ ...newAdmin, password: e.target.value })
              }
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #444",
                background: "#1a1a1a",
                color: "#fff",
              }}
            />
            <button
              onClick={handleAddAdmin}
              disabled={addingAdmin}
              style={{
                marginTop: "10px",
                background: "linear-gradient(90deg,#00d4ff,#00bcd4)",
                border: "none",
                padding: "10px",
                borderRadius: "8px",
                color: "#000",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              {addingAdmin ? "Creating..." : "Create Admin"}
            </button>
            <button
              onClick={() => document.getElementById("addAdminModal").close()}
              style={{
                background: "transparent",
                border: "none",
                color: "#aaa",
                marginTop: "5px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </dialog>
      </>
    );
  };

  // Purchases Page
  // ✅ Purchases Page
  const PurchasesPage = () => (
    <>
      <Title>Purchases</Title>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>User Name</th>
              <th>Email</th>
              <th>Item</th>
              <th>Price (₹)</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {purchases.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", color: "#aaa" }}>
                  No purchases yet.
                </td>
              </tr>
            ) : (
              purchases
                .sort(
                  (a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.() // sort latest first
                )
                .map((p) => (
                  <tr key={p.id}>
                    <td>{p.userName || "Unknown"}</td>
                    <td>{p.userEmail || "N/A"}</td>
                    <td>{p.codeTitle || "Untitled Item"}</td>
                    <td>₹{p.amount || 0}</td>
                    <td>
                      {p.createdAt
                        ? p.createdAt.toDate().toLocaleDateString()
                        : "-"}
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </>
  );

  const ContactsPage = () => {
    const [leadFilter, setLeadFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [expandedMessages, setExpandedMessages] = useState({});
    const copyPhone = async (phone) => {
      if (!phone) return;
      try {
        await navigator.clipboard.writeText(phone);
      } catch (err) {
        const input = document.createElement("input");
        input.value = phone;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      alert("Phone number copied");
    };
    const handleDeleteQuotation = async (lead) => {
      const label = lead.name || lead.email || "this quotation";
      if (
        !window.confirm(
          `Delete quotation?\n\n${label} ko permanently delete karna hai?`
        )
      ) {
        return;
      }

      try {
        await deleteDoc(doc(db, "quotations", lead.id));
        alert("Quotation deleted");
        fetchQuotations();
      } catch (err) {
        console.error(err);
        alert("Error deleting quotation");
      }
    };
    const handleDeleteContact = async (lead) => {
      const label = lead.name || lead.email || "this contact";
      if (
        !window.confirm(
          `Delete contact?\n\n${label} ko permanently delete karna hai?`
        )
      ) {
        return;
      }

      try {
        await deleteDoc(doc(db, "contacts", lead.id));
        alert("Contact deleted");
        fetchContacts();
      } catch (err) {
        console.error(err);
        alert("Error deleting contact");
      }
    };

    const leads = [
      ...quotations.map((item) => ({
        ...item,
        source: "Quotation",
      })),
      ...contacts.map((item) => ({
        ...item,
        source: "Contact",
      })),
    ].sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));
    const filteredLeads =
      leadFilter === "all"
        ? leads
        : leads.filter((lead) => lead.source === leadFilter);
    const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const paginatedLeads = filteredLeads.slice(startIndex, startIndex + pageSize);
    const showingFrom = filteredLeads.length === 0 ? 0 : startIndex + 1;
    const showingTo = Math.min(startIndex + pageSize, filteredLeads.length);
    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1)
      .filter(
        (page) =>
          page === 1 ||
          page === totalPages ||
          Math.abs(page - safePage) <= 1
      );

    const changeFilter = (filter) => {
      setLeadFilter(filter);
      setCurrentPage(1);
    };

    const changePageSize = (size) => {
      setPageSize(size);
      setCurrentPage(1);
    };
    const toggleMessage = (key) => {
      setExpandedMessages((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    };

    return (
      <>
        <LeadHeader>
          <div>
            <Title>Contact Quotations</Title>
            <LeadSubtitle>
              Website contact requests and quotation enquiries in one inbox.
            </LeadSubtitle>
          </div>
          <FilterTabs>
            <FilterButton
              active={leadFilter === "all"}
              onClick={() => changeFilter("all")}
            >
              All
            </FilterButton>
            <FilterButton
              active={leadFilter === "Quotation"}
              onClick={() => changeFilter("Quotation")}
            >
              Quotations
            </FilterButton>
            <FilterButton
              active={leadFilter === "Contact"}
              onClick={() => changeFilter("Contact")}
            >
              Contacts
            </FilterButton>
          </FilterTabs>
        </LeadHeader>

        <LeadStats>
          <LeadStat>
            <strong>{leads.length}</strong>
            <span>Total leads</span>
          </LeadStat>
          <LeadStat>
            <strong>{quotations.length}</strong>
            <span>Quotation requests</span>
          </LeadStat>
          <LeadStat>
            <strong>{contacts.length}</strong>
            <span>Contact messages</span>
          </LeadStat>
        </LeadStats>

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Source</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Service</th>
                <th>Budget</th>
                <th>Message</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", color: "#aaa" }}>
                    No leads found for this filter.
                  </td>
                </tr>
              ) : (
                paginatedLeads.map((lead) => {
                  const rowKey = `${lead.source}-${lead.id}`;
                  const message = lead.message || "-";
                  const canExpand = message.length > 120;
                  const isExpanded = !!expandedMessages[rowKey];

                  return (
                  <tr key={rowKey}>
                    <td>
                      <SourcePill type={lead.source}>{lead.source}</SourcePill>
                    </td>
                    <td>{lead.name || "-"}</td>
                    <td>
                      {lead.email ? (
                        <a
                          href={`mailto:${lead.email}`}
                          style={{ color: "#00d4ff", textDecoration: "underline" }}
                        >
                          {lead.email}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {lead.mobile || lead.phone || lead.contactNumber ? (
                        <PhoneCell>
                          <span>{lead.mobile || lead.phone || lead.contactNumber}</span>
                          <CopyButton
                            onClick={() =>
                              copyPhone(lead.mobile || lead.phone || lead.contactNumber)
                            }
                          >
                            Copy
                          </CopyButton>
                        </PhoneCell>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{normalizeService(lead.service || lead.modalType)}</td>
                    <td>{lead.budget || "Not specified"}</td>
                    <MessageCell>
                      <MessageText expanded={isExpanded}>{message}</MessageText>
                      {canExpand && (
                        <ViewMoreButton onClick={() => toggleMessage(rowKey)}>
                          {isExpanded ? "View less" : "View more"}
                        </ViewMoreButton>
                      )}
                    </MessageCell>
                    <td>{formatDate(lead.createdAt)}</td>
                    <td>
                      {lead.source === "Quotation" ? (
                        <ActionButton
                          type="delete"
                          onClick={() => handleDeleteQuotation(lead)}
                        >
                          Delete
                        </ActionButton>
                      ) : lead.source === "Contact" ? (
                        <ActionButton
                          type="delete"
                          onClick={() => handleDeleteContact(lead)}
                        >
                          Delete
                        </ActionButton>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          <PaginationBar>
            <PaginationInfo>
              Showing {showingFrom}-{showingTo} of {filteredLeads.length} leads
            </PaginationInfo>
            <PaginationControls>
              <PageSizeSelect
                value={pageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </PageSizeSelect>
              <PageButton
                disabled={safePage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                Prev
              </PageButton>
              {pageNumbers.map((page, index) => {
                const previousPage = pageNumbers[index - 1];
                const showGap = previousPage && page - previousPage > 1;

                return (
                  <React.Fragment key={page}>
                    {showGap && <PaginationInfo>...</PaginationInfo>}
                    <PageButton
                      active={page === safePage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </PageButton>
                  </React.Fragment>
                );
              })}
              <PageButton
                disabled={safePage === totalPages}
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
              >
                Next
              </PageButton>
            </PaginationControls>
          </PaginationBar>
        </TableWrapper>
      </>
    );
  };

  const PlanningPage = () => {
    const today = getTodayInput();
    const [reportRange, setReportRange] = useState({
      from: "",
      to: today,
      type: "all",
    });

    const filteredTasks = planningTasks.filter((task) => {
      if (reportRange.from && task.taskDate < reportRange.from) return false;
      if (reportRange.to && task.taskDate > reportRange.to) return false;
      if (reportRange.type !== "all" && (task.type || "projects") !== reportRange.type) {
        return false;
      }
      return true;
    });

    const sortedTasks = [...filteredTasks].sort((a, b) => {
      if ((b.taskDate || "") !== (a.taskDate || "")) {
        return String(b.taskDate || "").localeCompare(String(a.taskDate || ""));
      }
      return getTime(b.createdAt) - getTime(a.createdAt);
    });

    const completedCount = filteredTasks.filter((task) => task.completed).length;
    const missedCount = filteredTasks.filter(
      (task) => !task.completed && task.taskDate < today
    ).length;
    const pendingCount = filteredTasks.filter(
      (task) => !task.completed && task.taskDate >= today
    ).length;
    const groupedTasks = sortedTasks.reduce((groups, task) => {
      const dateKey = task.taskDate || "No Date";
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(task);
      return groups;
    }, {});
    const dayGroups = Object.entries(groupedTasks);

    const getTaskStatus = (task) => {
      if (task.completed) return "done";
      if (task.taskDate < today) return "missed";
      return "pending";
    };

    const formatPlanningDate = (date) => {
      if (!date || date === "No Date") return "No Date";
      return new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const formatTaskTime = (time) => {
      if (!time) return "";
      const [hours, minutes] = time.split(":");
      if (!hours || !minutes) return time;
      return new Date(`1970-01-01T${time}`).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const toggleTaskComplete = async (task) => {
      try {
        await updateDoc(doc(db, "planningTasks", task.id), {
          completed: !task.completed,
          completedAt: task.completed ? null : new Date(),
        });
        fetchPlanningTasks();
      } catch (err) {
        console.error(err);
        alert("Error updating task");
      }
    };

    const handleDeleteTask = async (id) => {
      if (!window.confirm("Delete task?\n\nIs task ko permanently delete karna hai?")) {
        return;
      }

      try {
        await deleteDoc(doc(db, "planningTasks", id));
        alert("Task deleted");
        fetchPlanningTasks();
      } catch (err) {
        console.error(err);
        alert("Error deleting task");
      }
    };

    return (
      <PlanningShell>
        <PlanningTopBar>
          <div>
            <Title style={{ marginBottom: "6px" }}>Planning</Title>
            <LeadSubtitle>Simple day-wise task list and progress report.</LeadSubtitle>
          </div>
          <AddButton
            onClick={() => navigate("/add-planning")}
            style={{ marginBottom: 0 }}
          >
            <Plus size={18} /> Add Task
          </AddButton>
        </PlanningTopBar>

        <PlanningReport>
          <PlanningReportRow>
            <PlanningReportFilters>
              <Title style={{ fontSize: "1.05rem", marginBottom: "8px" }}>
                Report Check
              </Title>
              <PlanningGrid>
                <PlanningField>
                  From
                  <PlanningInput
                    type="date"
                    value={reportRange.from}
                    onChange={(e) =>
                      setReportRange({ ...reportRange, from: e.target.value })
                    }
                  />
                </PlanningField>
                <PlanningField>
                  To
                  <PlanningInput
                    type="date"
                    value={reportRange.to}
                    onChange={(e) =>
                      setReportRange({ ...reportRange, to: e.target.value })
                    }
                  />
                </PlanningField>
                <PlanningField>
                  Type
                  <PlanningSelect
                    value={reportRange.type}
                    onChange={(e) =>
                      setReportRange({ ...reportRange, type: e.target.value })
                    }
                  >
                    <option value="all">All Types</option>
                    {planningTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </PlanningSelect>
                </PlanningField>
              </PlanningGrid>
            </PlanningReportFilters>
            <PlanningStatsRow>
              <PlanningMiniStat>
                <strong>{filteredTasks.length}</strong>
                <span>Total</span>
              </PlanningMiniStat>
              <PlanningMiniStat>
                <strong>{completedCount}</strong>
                <span>Completed</span>
              </PlanningMiniStat>
              <PlanningMiniStat>
                <strong>{missedCount}</strong>
                <span>Missed</span>
              </PlanningMiniStat>
              <PlanningMiniStat>
                <strong>{pendingCount}</strong>
                <span>Pending</span>
              </PlanningMiniStat>
            </PlanningStatsRow>
          </PlanningReportRow>
        </PlanningReport>

        {dayGroups.length === 0 ? (
          <TableWrapper>
            <p style={{ textAlign: "center", color: "#aaa" }}>
              No planning tasks added yet.
            </p>
          </TableWrapper>
        ) : (
          <PlanningDays>
            {dayGroups.map(([date, tasks]) => (
              <PlanningDayGroup key={date}>
                <PlanningDayHeader>
                  <div>
                    <h3>{formatPlanningDate(date)}</h3>
                    <span>
                      {tasks.length} task(s),{" "}
                      {tasks.filter((task) => task.completed).length} completed
                    </span>
                  </div>
                </PlanningDayHeader>
                <PlanningTaskList>
                  {tasks.map((task) => {
                    const status = getTaskStatus(task);
                    return (
                      <PlanningTaskItem key={task.id}>
                        <PlanningCheckbox
                          type="checkbox"
                          checked={Boolean(task.completed)}
                          onChange={() => toggleTaskComplete(task)}
                          aria-label="Mark task complete"
                        />
                        <PlanningTaskBody completed={task.completed}>
                          <strong>{task.title || "-"}</strong>
                          {task.details && <p>{task.details}</p>}
                          <PlanningTaskMeta>
                          <StatusPill status={status}>
                            {status === "done"
                              ? "Completed"
                              : status === "missed"
                              ? "Missed"
                              : "Pending"}
                          </StatusPill>
                            <TypePill>
                              {getPlanningTypeLabel(task.type || "projects")}
                            </TypePill>
                            {task.taskTime && (
                              <TimePill>{formatTaskTime(task.taskTime)}</TimePill>
                            )}
                            {task.completedAt && (
                              <LeadSubtitle>
                                Completed {formatDate(task.completedAt)}
                              </LeadSubtitle>
                            )}
                          </PlanningTaskMeta>
                        </PlanningTaskBody>
                        <PlanningTaskActions>
                          {task.completed && (
                            <PlanningIconButton onClick={() => toggleTaskComplete(task)}>
                              Undo
                            </PlanningIconButton>
                          )}
                          <PlanningIconButton
                            danger
                            onClick={() => handleDeleteTask(task.id)}
                          >
                            Delete
                          </PlanningIconButton>
                        </PlanningTaskActions>
                      </PlanningTaskItem>
                    );
                  })}
                </PlanningTaskList>
              </PlanningDayGroup>
            ))}
          </PlanningDays>
        )}
      </PlanningShell>
    );
  };

  const DemosPage = () => {
    const handleDeleteDemo = async (id) => {
      if (!window.confirm("Delete demo?\n\nIs demo ko permanently delete karna hai?")) {
        return;
      }

      try {
        await deleteDoc(doc(db, "demos", id));
        alert("Demo deleted");
        fetchDemos();
      } catch (err) {
        console.error(err);
        alert("Error deleting demo");
      }
    };

    const sortedDemos = [...demos].sort(
      (a, b) => getTime(b.createdAt) - getTime(a.createdAt)
    );

    return (
      <>
        <LeadHeader>
          <div>
            <Title>Demos</Title>
            <LeadSubtitle>
              Manage uploaded demo videos, screenshots, descriptions and price range.
            </LeadSubtitle>
          </div>
          <AddButton onClick={() => navigate("/add-demo")}>
            <Plus size={18} /> Add New Demo
          </AddButton>
        </LeadHeader>

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Demo</th>
                <th>Price Range</th>
                <th>Images</th>
                <th>Video</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedDemos.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", color: "#aaa" }}>
                    No demos uploaded yet.
                  </td>
                </tr>
              ) : (
                sortedDemos.map((demo) => (
                  <tr key={demo.id}>
                    <td>
                      <strong>{demo.title || "-"}</strong>
                      <MessageText expanded={false}>
                        {demo.description || "-"}
                      </MessageText>
                    </td>
                    <td>{demo.priceRange || "-"}</td>
                    <td>
                      <MediaPreviewGrid>
                        {(demo.imageURLs || []).slice(0, 4).map((url) => (
                          <a href={url} target="_blank" rel="noreferrer" key={url}>
                            <MediaThumb src={url} alt={demo.title || "Demo image"} />
                          </a>
                        ))}
                        {(demo.imageURLs || []).length > 4 && (
                          <FileHint>+{demo.imageURLs.length - 4} more</FileHint>
                        )}
                      </MediaPreviewGrid>
                    </td>
                    <td>
                      {demo.videoURL ? (
                        <DemoVideo src={demo.videoURL} controls />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{formatDate(demo.createdAt)}</td>
                    <td>
                      <ActionButton
                        type="delete"
                        onClick={() => handleDeleteDemo(demo.id)}
                      >
                        Delete
                      </ActionButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };

  const CareersPage = () => {
    const handleDelete = async (id) => {
      if (!window.confirm("Delete opening?\n\nIs career opening ko permanently delete karna hai?")) {
        return;
      }

      try {
        await deleteDoc(doc(db, "careers", id));
        alert("Career opening deleted");
        fetchCareers();
      } catch (err) {
        console.error(err);
        alert("Error deleting career opening");
      }
    };

    const sortedCareers = [...careers].sort(
      (a, b) => getTime(b.createdAt) - getTime(a.createdAt)
    );
    const sortedApplications = [...careerApplications].sort(
      (a, b) => getTime(b.createdAt || b.appliedAt) - getTime(a.createdAt || a.appliedAt)
    );

    return (
      <>
        <LeadHeader>
          <div>
            <Title>Careers</Title>
            <LeadSubtitle>Manage job openings and applications.</LeadSubtitle>
          </div>
          <AddButton onClick={() => navigate("/add-career")}>
            <Plus size={18} /> Add Opening
          </AddButton>
        </LeadHeader>

        <TableWrapper>
          <Title style={{ fontSize: "1.2rem", marginBottom: "14px" }}>
            Openings
          </Title>
          <Table>
            <thead>
              <tr>
                <th>Opening</th>
                <th>Location</th>
                <th>Type</th>
                <th>Experience</th>
                <th>Salary</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedCareers.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", color: "#aaa" }}>
                    No career openings added yet.
                  </td>
                </tr>
              ) : (
                sortedCareers.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.title || "-"}</strong>
                      <MessageText expanded={false}>{item.description || "-"}</MessageText>
                    </td>
                    <td>{item.location || "-"}</td>
                    <td>{item.jobType || "-"}</td>
                    <td>{item.experience || "-"}</td>
                    <td>{item.salaryRange || "-"}</td>
                    <td>{item.status || "-"}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>
                      <ActionButton type="delete" onClick={() => handleDelete(item.id)}>
                        Delete
                      </ActionButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>

        <TableWrapper style={{ marginTop: "22px" }}>
          <Title style={{ fontSize: "1.2rem", marginBottom: "14px" }}>
            Applied By
          </Title>
          <Table>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Opening</th>
                <th>Resume</th>
                <th>Message</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {sortedApplications.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", color: "#aaa" }}>
                    No career applications yet.
                  </td>
                </tr>
              ) : (
                sortedApplications.map((app) => (
                  <tr key={app.id}>
                    <td>{app.name || app.candidateName || "-"}</td>
                    <td>{app.email || "-"}</td>
                    <td>{app.phone || app.mobile || "-"}</td>
                    <td>{app.openingTitle || app.jobTitle || app.careerTitle || app.careerId || "-"}</td>
                    <td>
                      {app.resumeURL || app.resumeUrl || app.cvURL ? (
                        <a
                          href={app.resumeURL || app.resumeUrl || app.cvURL}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#00d4ff", textDecoration: "underline" }}
                        >
                          View
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <MessageCell>
                      <MessageText expanded={false}>
                        {app.message || app.coverLetter || "-"}
                      </MessageText>
                    </MessageCell>
                    <td>{formatDate(app.createdAt || app.appliedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };

  const FAQsPage = () => {
    const handleDelete = async (id) => {
      if (!window.confirm("Delete FAQ?\n\nIs FAQ ko permanently delete karna hai?")) {
        return;
      }

      try {
        await deleteDoc(doc(db, "faqs", id));
        alert("FAQ deleted");
        fetchFaqs();
      } catch (err) {
        console.error(err);
        alert("Error deleting FAQ");
      }
    };

    const sortedFaqs = [...faqs].sort((a, b) => {
      const orderDiff = (a.sortOrder || 0) - (b.sortOrder || 0);
      return orderDiff || getTime(b.createdAt) - getTime(a.createdAt);
    });

    return (
      <>
        <LeadHeader>
          <div>
            <Title>FAQs</Title>
            <LeadSubtitle>Manage frequently asked questions.</LeadSubtitle>
          </div>
          <AddButton onClick={() => navigate("/add-faq")}>
            <Plus size={18} /> Add FAQ
          </AddButton>
        </LeadHeader>

        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Category</th>
                <th>Question</th>
                <th>Answer</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedFaqs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", color: "#aaa" }}>
                    No FAQs added yet.
                  </td>
                </tr>
              ) : (
                sortedFaqs.map((item) => (
                  <tr key={item.id}>
                    <td>{item.sortOrder || 0}</td>
                    <td>{item.category || "-"}</td>
                    <td>{item.question || "-"}</td>
                    <MessageCell>
                      <MessageText expanded={false}>{item.answer || "-"}</MessageText>
                    </MessageCell>
                    <td>{item.status || "-"}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>
                      <ActionButton type="delete" onClick={() => handleDelete(item.id)}>
                        Delete
                      </ActionButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };

  const ReadyCodesPage = () => {
    const handleDelete = async (id) => {
      if (
        window.confirm(
          "Are you sure you want to delete this Ready-to-Use Code?"
        )
      ) {
        try {
          await deleteDoc(doc(db, "readyCodes", id));
          alert("Code deleted ❌");
          fetchReadyCodes();
        } catch (err) {
          console.error(err);
          alert("Error deleting ready code");
        }
      }
    };

    return (
      <>
        <Title>Ready-to-Use Codes</Title>
        <AddButton onClick={() => navigate("/add-readycode")}>
          <Plus size={18} /> Add Ready Code
        </AddButton>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Price</th>
                <th>Date</th>
                <th>File</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {readyCodes.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", color: "#aaa" }}
                  >
                    No ready-to-use codes added yet.
                  </td>
                </tr>
              ) : (
                readyCodes.map((r) => (
                  <tr key={r.id}>
                    <td
                      style={{
                        color: "#58a6ff",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => navigate(`/readycode/${r.id}`)}
                    >
                      {r.title}
                    </td>
                    <td>{r.category || "General"}</td>
                    <td>{r.price === 0 ? "Free" : `₹${r.price}`}</td>
                    <td>{r.createdAt?.toDate().toLocaleDateString()}</td>
                    <td>
                      <a
                        href={r.fileURL}
                        download
                        style={{
                          color: "#00d4ff",
                          textDecoration: "underline",
                        }}
                      >
                        Download
                      </a>
                    </td>
                    <td>
                      <ActionButton
                        type="edit"
                        onClick={() => navigate(`/edit-readycode/${r.id}`)}
                      >
                        Edit
                      </ActionButton>
                    </td>
                    <td>
                      <ActionButton
                        type="delete"
                        onClick={() => handleDelete(r.id)}
                      >
                        Delete
                      </ActionButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableWrapper>
      </>
    );
  };

  return (
    <Container>
      <MobileTopBar>
        <MobileBrand>
          <img src="/logo.png" alt="Admin Logo" />
          <span>Admin Panel</span>
        </MobileBrand>
        <MobileMenuButton
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </MobileMenuButton>
      </MobileTopBar>
      <SidebarBackdrop
        type="button"
        $open={sidebarOpen}
        onClick={() => setSidebarOpen(false)}
        aria-label="Close menu"
      />
      {/* Sidebar */}
      <Sidebar $open={sidebarOpen}>
        <SidebarCloseButton
          type="button"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        >
          <X size={18} />
        </SidebarCloseButton>
        <LogoWrapper>
          <img src="/logo.png" alt="Admin Logo" />{" "}
          {/* Replace with your logo path */}
        </LogoWrapper>
        {/* <SidebarHeader>Admin Panel</SidebarHeader> */}
        <SidebarItem
          active={activePage === "dashboard"}
          onClick={() => switchPage("dashboard")}
        >
          <LayoutDashboard size={18} /> Dashboard
        </SidebarItem>
        <SidebarItem
          active={activePage === "users"}
          onClick={() => switchPage("users")}
        >
          <Users size={18} /> Users
        </SidebarItem>
        <SidebarItem
          active={activePage === "courses"}
          onClick={() => switchPage("courses")}
        >
          <BookOpen size={18} /> Courses
        </SidebarItem>
        <SidebarItem
          active={activePage === "purchases"}
          onClick={() => switchPage("purchases")}
        >
          <ShoppingCart size={18} /> Purchases
        </SidebarItem>
        <SidebarItem
          active={activePage === "contacts"}
          onClick={() => switchPage("contacts")}
        >
          <Mail size={18} /> Quotations
        </SidebarItem>
        <SidebarItem
          active={activePage === "planning"}
          onClick={() => switchPage("planning")}
        >
          <CalendarCheck size={18} /> Planning
        </SidebarItem>
        <SidebarItem
          active={activePage === "demos"}
          onClick={() => switchPage("demos")}
        >
          <Upload size={18} /> Demos
        </SidebarItem>
        <SidebarItem
          active={activePage === "careers"}
          onClick={() => switchPage("careers")}
        >
          <BriefcaseBusiness size={18} /> Careers
        </SidebarItem>
        <SidebarItem
          active={activePage === "faqs"}
          onClick={() => switchPage("faqs")}
        >
          <HelpCircle size={18} /> FAQs
        </SidebarItem>
        <SidebarItem
          active={activePage === "blogs"}
          onClick={() => switchPage("blogs")}
        >
          <FileText size={18} /> Blogs
        </SidebarItem>
        <SidebarItem
          active={activePage === "codeshop"}
          onClick={() => switchPage("codeshop")}
        >
          <Archive size={18} /> CodeShop
        </SidebarItem>
        <SidebarItem
          active={activePage === "interviewPreps"}
          onClick={() => switchPage("interviewPreps")}
        >
          <ClipboardList size={18} /> Interview Preps
        </SidebarItem>
        <SidebarItem
          active={activePage === "readyCodes"}
          onClick={() => switchPage("readyCodes")}
        >
          <Archive size={18} /> Ready Codes
        </SidebarItem>

        <SidebarItem
          onClick={() => {
            setSidebarOpen(false);
            setShowLogoutModal(true);
          }}
          style={{ marginBottom: "24px" }}
        >
          <LogOut size={18} /> Logout
        </SidebarItem>
      </Sidebar>

      {/* Content */}
      <Content>
        {activePage === "dashboard" && <DashboardPage />}
        {activePage === "users" && <UsersPage />}
        {activePage === "courses" && <CoursesPage />}
        {activePage === "purchases" && <PurchasesPage />}
        {activePage === "contacts" && <ContactsPage />}
        {activePage === "planning" && <PlanningPage />}
        {activePage === "demos" && <DemosPage />}
        {activePage === "careers" && <CareersPage />}
        {activePage === "faqs" && <FAQsPage />}
        {activePage === "blogs" && <BlogsPage />}
        {activePage === "codeshop" && <CodeShopPage />}
        {activePage === "interviewPreps" && <InterviewPrepsPage />}
        {activePage === "readyCodes" && <ReadyCodesPage />} {/* 🆕 */}
      </Content>
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false);
          logout();
          navigate("/");
        }}
      />
    </Container>
  );
};

export default AdminPanel;
